import { useEffect, useState } from 'react';
import { KeyRound, Plus, ShieldCheck, ShieldHalf, UserX, UserCheck } from 'lucide-react';
import {
  ACCIONES_ADMIN,
  MODULOS_ADMIN,
  UserStatus,
  type AccionAdmin,
  type ModuloAdmin,
  type PermisosAdmin,
} from '@kick/types';
import { getErrorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth-store';
import { cn, desdeHace } from '@/lib/cn';
import {
  useCambiarEstadoAdmin,
  useCambiarPermisosAdmin,
  useCrearAdmin,
  useEquipo,
  type AdministradorEquipo,
  type Credenciales,
} from '@/lib/queries';
import {
  Boton,
  Campo,
  Cargando,
  ConfirmarModal,
  Entrada,
  ErrorCarga,
  Interruptor,
  Modal,
} from '@/components/ui';
import { TarjetaCredenciales } from '@/components/TarjetaCredenciales';
import { useAvisos } from '@/components/avisos';

/**
 * El equipo de administración.
 *
 * Aquí el dueño crea a quien le ayuda a operar la plataforma y decide, módulo
 * por módulo y acción por acción, qué puede hacer. Lo que no se marca no
 * existe para esa persona: ni en el menú ni en la API.
 *
 * Solo entra quien tiene acceso total; un administrador restringido ni ve el
 * enlace. Y nadie se edita a sí mismo: quitarse el acceso total por error
 * dejaría la plataforma sin dueño.
 */
export function Equipo() {
  const equipo = useEquipo();
  const yo = useAuth((s) => s.user);
  const [creando, setCreando] = useState(false);
  const [editando, setEditando] = useState<AdministradorEquipo | null>(null);

  if (equipo.isLoading) return <Cargando texto="Cargando el equipo…" />;
  if (equipo.isError) {
    return (
      <ErrorCarga mensaje={getErrorMessage(equipo.error)} onReintentar={() => void equipo.refetch()} />
    );
  }

  const administradores = equipo.data ?? [];

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl text-kick-dark">Equipo</h1>
          <p className="mt-1 text-sm text-kick-gray">
            Quién administra la plataforma y qué puede hacer cada uno.
          </p>
        </div>
        <Boton icono={<Plus className="h-4 w-4" />} onClick={() => setCreando(true)}>
          Nuevo administrador
        </Boton>
      </div>

      <ul className="mt-6 space-y-2">
        {administradores.map((admin) => {
          const soyYo = admin.id === yo?.id;
          const accesoTotal = admin.adminPermissions === null;
          const modulos = accesoTotal ? 0 : Object.keys(admin.adminPermissions ?? {}).length;
          const suspendido = admin.status !== UserStatus.ACTIVE;

          return (
            <li key={admin.id}>
              <button
                type="button"
                disabled={soyYo}
                onClick={() => setEditando(admin)}
                className={cn(
                  'card-kick flex w-full items-center gap-4 p-4 text-left transition',
                  soyYo ? 'cursor-default' : 'hover:bg-kick-light',
                  suspendido && 'opacity-60',
                )}
              >
                <span
                  className={cn(
                    'grid h-11 w-11 shrink-0 place-items-center rounded-full',
                    accesoTotal ? 'bg-kick-red text-white' : 'bg-kick-light text-kick-gray',
                  )}
                >
                  {accesoTotal ? (
                    <ShieldCheck className="h-5 w-5" />
                  ) : (
                    <ShieldHalf className="h-5 w-5" />
                  )}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-kick-ink">{admin.fullName}</span>
                    {soyYo && (
                      <span className="rounded-full bg-kick-light px-2 py-0.5 text-xs text-kick-gray">
                        tú
                      </span>
                    )}
                    {suspendido && (
                      <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                        suspendido
                      </span>
                    )}
                    {admin.mustChangePassword && (
                      <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                        <KeyRound className="h-3 w-3" />
                        clave temporal
                      </span>
                    )}
                  </span>
                  <span className="block text-sm text-kick-gray">
                    @{admin.username} ·{' '}
                    {accesoTotal
                      ? 'acceso total'
                      : `${modulos} módulo${modulos === 1 ? '' : 's'}`}
                    {admin.lastLoginAt
                      ? ` · entró ${desdeHace(admin.lastLoginAt)}`
                      : ' · nunca ha entrado'}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <ModalCrear abierto={creando} onCerrar={() => setCreando(false)} />
      <ModalEditar admin={editando} onCerrar={() => setEditando(null)} />
    </div>
  );
}

// ── Matriz de permisos ──────────────────────────────────────

/**
 * La matriz módulo × acción.
 *
 * «Ver» es la puerta: apagarla apaga el módulo entero; encender cualquier otra
 * acción enciende «ver» sola, porque una acción sin entrada no sirve de nada.
 * El interruptor de acceso total deja la matriz de lado: el dueño no se
 * describe con casillas.
 */
function MatrizPermisos({
  accesoTotal,
  permisos,
  onCambio,
}: {
  accesoTotal: boolean;
  permisos: PermisosAdmin;
  onCambio: (accesoTotal: boolean, permisos: PermisosAdmin) => void;
}) {
  const alternar = (modulo: ModuloAdmin, accion: AccionAdmin): void => {
    const actuales = new Set(permisos[modulo] ?? []);

    if (actuales.has(accion)) {
      actuales.delete(accion);
      if (accion === 'ver') actuales.clear();
    } else {
      actuales.add(accion);
      actuales.add('ver');
    }

    const siguiente = { ...permisos };
    if (actuales.size === 0) delete siguiente[modulo];
    else siguiente[modulo] = [...actuales];
    onCambio(false, siguiente);
  };

  const todoElModulo = (modulo: ModuloAdmin, acciones: readonly AccionAdmin[]): void => {
    const completo = (permisos[modulo]?.length ?? 0) === acciones.length;
    const siguiente = { ...permisos };
    if (completo) delete siguiente[modulo];
    else siguiente[modulo] = [...acciones];
    onCambio(false, siguiente);
  };

  return (
    <div>
      <label className="flex items-center gap-3 rounded-kick border border-kick-gray/20 p-3">
        <Interruptor
          activo={accesoTotal}
          etiqueta="Acceso total"
          onCambio={(v) => onCambio(v, permisos)}
        />
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-kick-ink">Acceso total</span>
          <span className="block text-xs text-kick-gray">
            Lo mismo que tú: todos los módulos, y puede crear y editar administradores.
          </span>
        </span>
      </label>

      <div
        className={cn(
          'mt-3 divide-y divide-kick-gray/10 rounded-kick border border-kick-gray/20',
          accesoTotal && 'pointer-events-none opacity-40',
        )}
        aria-disabled={accesoTotal}
      >
        {MODULOS_ADMIN.map((modulo) => {
          const marcadas = new Set(permisos[modulo.key] ?? []);
          const completo = marcadas.size === modulo.acciones.length;

          return (
            <fieldset key={modulo.key} className="p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <legend className="min-w-0">
                  <span className="block text-sm font-semibold text-kick-ink">{modulo.label}</span>
                  <span className="block text-xs text-kick-gray">{modulo.descripcion}</span>
                </legend>
                <button
                  type="button"
                  onClick={() => todoElModulo(modulo.key, modulo.acciones)}
                  className="text-xs font-semibold text-kick-red hover:underline"
                >
                  {completo ? 'Quitar todo' : 'Marcar todo'}
                </button>
              </div>

              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5">
                {modulo.acciones.map((accion) => (
                  <label
                    key={accion}
                    className="flex cursor-pointer items-center gap-1.5 text-sm text-kick-ink"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-kick-gray/40 text-kick-red focus:ring-kick-red"
                      checked={marcadas.has(accion)}
                      onChange={() => alternar(modulo.key, accion)}
                      aria-label={`${modulo.label}: ${ACCIONES_ADMIN[accion]}`}
                    />
                    {ACCIONES_ADMIN[accion]}
                  </label>
                ))}
              </div>
            </fieldset>
          );
        })}
      </div>
    </div>
  );
}

// ── Crear ───────────────────────────────────────────────────

function ModalCrear({ abierto, onCerrar }: { abierto: boolean; onCerrar: () => void }) {
  const avisos = useAvisos();
  const crear = useCrearAdmin();
  const [datos, setDatos] = useState({ username: '', fullName: '', phone: '', email: '' });
  const [accesoTotal, setAccesoTotal] = useState(false);
  const [permisos, setPermisos] = useState<PermisosAdmin>({});
  const [credenciales, setCredenciales] = useState<Credenciales | null>(null);

  useEffect(() => {
    if (!abierto) return;
    setDatos({ username: '', fullName: '', phone: '', email: '' });
    setAccesoTotal(false);
    setPermisos({});
    setCredenciales(null);
  }, [abierto]);

  const sinPermisos = !accesoTotal && Object.keys(permisos).length === 0;
  const incompleto = datos.username.trim().length < 3 || datos.fullName.trim().length < 3;

  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      titulo={credenciales ? 'Administrador creado' : 'Nuevo administrador'}
      descripcion={
        credenciales
          ? undefined
          : 'Entra por el mismo panel con una contraseña temporal que deberá cambiar.'
      }
      ancho="lg"
    >
      {/* La matriz es alta: el cuerpo se desplaza y los botones siempre quedan a la vista. */}
      <div className="max-h-[75vh] overflow-y-auto p-5">
        {credenciales ? (
          <>
            <TarjetaCredenciales credenciales={credenciales} />
            <div className="mt-6 flex justify-end">
              <Boton onClick={onCerrar}>Listo</Boton>
            </div>
          </>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo etiqueta="Nombre completo" requerido>
                <Entrada
                  value={datos.fullName}
                  onChange={(e) => setDatos({ ...datos, fullName: e.target.value })}
                  placeholder="Laura Gómez"
                  maxLength={150}
                />
              </Campo>
              <Campo etiqueta="Usuario" requerido ayuda="Con este entra al panel">
                <Entrada
                  value={datos.username}
                  onChange={(e) =>
                    setDatos({ ...datos, username: e.target.value.toLowerCase().trim() })
                  }
                  placeholder="lgomez"
                  maxLength={30}
                />
              </Campo>
              <Campo etiqueta="Teléfono">
                <Entrada
                  value={datos.phone}
                  onChange={(e) => setDatos({ ...datos, phone: e.target.value })}
                  placeholder="3001234567"
                  maxLength={20}
                />
              </Campo>
              <Campo etiqueta="Correo" ayuda="Para recuperar la contraseña">
                <Entrada
                  type="email"
                  value={datos.email}
                  onChange={(e) => setDatos({ ...datos, email: e.target.value })}
                  placeholder="laura@kickdomicilios.com"
                />
              </Campo>
            </div>

            <h3 className="mt-6 text-sm font-semibold uppercase tracking-wide text-kick-gray">
              Qué puede hacer
            </h3>
            <div className="mt-2">
              <MatrizPermisos
                accesoTotal={accesoTotal}
                permisos={permisos}
                onCambio={(total, p) => {
                  setAccesoTotal(total);
                  setPermisos(p);
                }}
              />
            </div>

            {sinPermisos && (
              <p className="mt-3 text-sm text-amber-800">
                Sin ningún permiso marcado solo podría entrar y ver la portada.
              </p>
            )}

            <div className="mt-6 flex justify-end gap-2">
              <Boton variante="secundario" onClick={onCerrar}>
                Cancelar
              </Boton>
              <Boton
                disabled={incompleto}
                cargando={crear.isPending}
                onClick={() =>
                  crear.mutate(
                    {
                      username: datos.username.trim(),
                      fullName: datos.fullName.trim(),
                      phone: datos.phone.trim() || undefined,
                      email: datos.email.trim() || undefined,
                      permissions: accesoTotal ? null : permisos,
                    },
                    {
                      onSuccess: (r) => {
                        setCredenciales(r.credenciales);
                        avisos.exito('Administrador creado');
                      },
                      onError: (error) => avisos.error(getErrorMessage(error)),
                    },
                  )
                }
              >
                Crear administrador
              </Boton>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ── Editar ──────────────────────────────────────────────────

function ModalEditar({
  admin,
  onCerrar,
}: {
  admin: AdministradorEquipo | null;
  onCerrar: () => void;
}) {
  const avisos = useAvisos();
  const cambiarPermisos = useCambiarPermisosAdmin();
  const cambiarEstado = useCambiarEstadoAdmin();
  const [accesoTotal, setAccesoTotal] = useState(false);
  const [permisos, setPermisos] = useState<PermisosAdmin>({});
  const [confirmandoEstado, setConfirmandoEstado] = useState(false);

  useEffect(() => {
    if (!admin) return;
    setAccesoTotal(admin.adminPermissions === null);
    setPermisos(admin.adminPermissions ?? {});
    setConfirmandoEstado(false);
  }, [admin]);

  if (!admin) return null;

  const suspendido = admin.status !== UserStatus.ACTIVE;
  const sinCambios =
    accesoTotal === (admin.adminPermissions === null) &&
    JSON.stringify(permisos) === JSON.stringify(admin.adminPermissions ?? {});

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo={admin.fullName}
      descripcion={`@${admin.username} · los cambios rigen en segundos, sin que cierre sesión.`}
      ancho="lg"
    >
      <div className="max-h-[75vh] overflow-y-auto p-5">
        <MatrizPermisos
          accesoTotal={accesoTotal}
          permisos={permisos}
          onCambio={(total, p) => {
            setAccesoTotal(total);
            setPermisos(p);
          }}
        />

        <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
          <Boton
            variante={suspendido ? 'secundario' : 'peligro'}
            icono={
              suspendido ? <UserCheck className="h-4 w-4" /> : <UserX className="h-4 w-4" />
            }
            onClick={() => setConfirmandoEstado(true)}
          >
            {suspendido ? 'Reactivar' : 'Suspender'}
          </Boton>

          <div className="flex gap-2">
            <Boton variante="secundario" onClick={onCerrar}>
              Cancelar
            </Boton>
            <Boton
              disabled={sinCambios}
              cargando={cambiarPermisos.isPending}
              onClick={() =>
                cambiarPermisos.mutate(
                  { id: admin.id, permissions: accesoTotal ? null : permisos },
                  {
                    onSuccess: () => {
                      avisos.exito('Permisos actualizados');
                      onCerrar();
                    },
                    onError: (error) => avisos.error(getErrorMessage(error)),
                  },
                )
              }
            >
              Guardar permisos
            </Boton>
          </div>
        </div>
      </div>

      <ConfirmarModal
        abierto={confirmandoEstado}
        titulo={suspendido ? `Reactivar a ${admin.fullName}` : `Suspender a ${admin.fullName}`}
        mensaje={
          suspendido
            ? 'Volverá a poder entrar al panel con los permisos que tenga.'
            : 'Se cierran sus sesiones de inmediato y no podrá volver a entrar hasta reactivarla.'
        }
        textoConfirmar={suspendido ? 'Reactivar' : 'Suspender'}
        cargando={cambiarEstado.isPending}
        onCancelar={() => setConfirmandoEstado(false)}
        onConfirmar={() =>
          cambiarEstado.mutate(
            { id: admin.id, status: suspendido ? UserStatus.ACTIVE : UserStatus.SUSPENDED },
            {
              onSuccess: () => {
                avisos.exito(suspendido ? 'Cuenta reactivada' : 'Cuenta suspendida');
                onCerrar();
              },
              onError: (error) => avisos.error(getErrorMessage(error)),
            },
          )
        }
      />
    </Modal>
  );
}
